import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '@components/button';
import { IconHire } from '@components/icons/hire';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Loading } from 'components/Loading';
import { useAuthenticatedUser } from 'features/authentication';
import { Modal, ModalProps, useModal } from 'features/modals';
import { useStore } from 'models/Provider';

type Props = ModalProps<{
  applicationId: number;
  applicationName: string;
  jobId: number;
  openOnboardModal?: boolean;
}>;

export const HireApplicantModal = (props: Props) => {
  const { applicationId, applicationName, jobId, closeModal, openOnboardModal } = props;
  const { showError, showSuccess } = useToaster();
  const { showModal } = useModal();
  const me = useAuthenticatedUser();
  const { applicationStore, t } = useStore();
  const queryClient = useQueryClient();

  const submit = useMutation(() => api.applications.hire(applicationId), {
    onSuccess: () => {
      applicationStore.load(applicationId);
      setTimeout(() => queryClient.invalidateQueries(['applicants']), 2000);
      applicationStore.hire(applicationId, me);
      showSuccess(t('application.hire.success', applicationName));
      if (openOnboardModal) {
        showModal('ONBOARD_APPLICANT', {
          _container: {
            autoWidth: false,
          },
          applicationId: applicationId,
          jobId: jobId,
        });
      } else {
        closeModal();
      }
    },
    onError: () => {
      showError(t('application.hire.error', applicationName));
    },
  });

  return (
    <Modal>
      <Modal.Header text={t('hiremodal.title')} />
      <Stack space={4}>
        <Text text={t('hiremodal.confirmtext')} />
        <Text font="bold16" text={applicationName} />
        {submit.isLoading && <Loading />}
      </Stack>

      <Modal.Actions>
        <Button
          isDisabled={submit.isLoading}
          variant="secondary"
          onClick={closeModal}
          text={t('hiremodal.button.cancel')}
        />

        <Button
          isDisabled={submit.isLoading}
          iconLeft={<IconHire />}
          onClick={() => submit.mutate()}
          text={t('hiremodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
};
